import React, { Fragment, useReducer } from "react";
import ReactModal from "react-modal";
import moment from "moment-timezone";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import Select from "react-select";

import TextInput from "../text-input";
import genders from "../../helpers/genders";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        dateOfBirth: null,
        weight: null,
        gender: null,
      };
    }
    default:
      return state;
  }
};

const SingleProspectPassengersTable = (props) => {
  const { passengers = [], handleChange, handleToggleEdit, disabled = false } = props;

  const initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: undefined,
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: null,
    weight: null,
    gender: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleAddPassenger = (e) => {
    e.preventDefault();

    handleToggleEdit("passengers");
    dispatch({
      type: "openModal",
    });
  };

  const handleEditPassenger = (e, index) => {
    e.preventDefault();

    const passenger = passengers[index];
    if (!passenger) {
      return;
    }

    handleToggleEdit("passengers");
    dispatch({
      type: "openModal",
      payload: {
        ...passenger,
        dateOfBirth: passenger.dateOfBirth ? new Date(passenger.dateOfBirth) : null,
        gender: genders.find(({ value }) => value === passenger.gender),
      },
    });
  };

  const handleDeletePassenger = (e, index) => {
    e.preventDefault();

    const passenger = passengers[index];
    if (!passenger) {
      return;
    }

    handleToggleEdit("passengers");
    dispatch({
      type: "openDeleteModal",
      payload: passenger,
    });
  };

  const handleCloseModal = () => {
    handleToggleEdit();
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleGenderChange = (gender) => {
    dispatch({
      type: "fieldChanged",
      field: "gender",
      value: gender,
    });
  };

  const handleUpdatePassenger = async (e) => {
    e.preventDefault();

    if (!state.firstName || !state.lastName) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }
    if (state.dateOfBirth && (moment(state.dateOfBirth).isAfter(moment(), "day") || moment(state.dateOfBirth).isBefore(moment('1900-01-01'), "day"))) {
      return toast.error(
        "Please fill valid date of birth and retry.",
        toastOptions,
      );
    }
    if (state.weight && (state.weight < 2 || state.weight > 660 || !/^-?\d+(\.\d{1})?$/.test(state.weight))) {
      return toast.error(
        "Please fill valid weight and retry.",
        toastOptions,
      );
    }

    const passenger = {
      _id: state._id,
      firstName: state.firstName,
      middleName: state.middleName,
      lastName: state.lastName,
      dateOfBirth: state.dateOfBirth || null,
      weight: state.weight || null,
      gender: state.gender?.value || null,
    };

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange(
      state._id ?
        (passengers || []).map((item) => item._id === state._id ? passenger : item) :
        [...(passengers || []), passenger],
    );
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  const handleDeletePassengerConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });
    await handleChange((passengers || []).filter((item) => item._id !== state._id));
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    handleCloseModal();
  };

  return (
    <Fragment>
      <h4>Saved Passengers</h4>

      <div className="prospect-table prospect-passengers-table">
        <table>
          <thead>
          <tr>
            <th>First Name</th>
            <th>Middle Name</th>
            <th>Last Name</th>
            <th>DOB</th>
            <th>Weight</th>
            <th>Gender</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          {!passengers?.length && <tr>
            <td colSpan={7}>No passengers are saved yet</td>
            <td/>
          </tr>}
          {passengers?.map((({ _id, firstName, middleName, lastName, dateOfBirth, weight, gender }, index) => (
            <tr key={_id}>
              <td>{firstName}</td>
              <td>{middleName}</td>
              <td>{lastName}</td>
              <td>{dateOfBirth ? moment(dateOfBirth).format("M/D/YYYY") : ""}</td>
              <td>{weight ? `${weight}lb` : ""}</td>
              <td>{gender}</td>
              <td>
                <button
                  className="base-button icon-button evo-blue"
                  onClick={(e) => {
                    handleEditPassenger(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">edit</span>
                </button>

                <button
                  className="base-button icon-button btn-danger ml-2"
                  onClick={(e) => {
                    handleDeletePassenger(e, index);
                  }}
                  disabled={disabled}
                >
                  <span className="material-icons">delete</span>
                </button>
              </td>
            </tr>
          )))}
          </tbody>
        </table>
      </div>

      <button
        className="mt-20 action-button base-button base-blue-button"
        onClick={handleAddPassenger}
        disabled={disabled}
      >
        Add Passenger
      </button>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{state._id ? "Edit" : "Add"} Passenger</h4>
        </div>

        <div className="modal-body base-modal">
          <form onSubmit={handleUpdatePassenger} className="w-100 base-form mt-20">
            <div className="w-100 d-flex gap-20">
              <TextInput
                name="firstName"
                label="First Name"
                required
                handleChange={handleInputChange}
                value={state.firstName}
                containerClasses="flex-half"
              />

              <div className="flex-half form-group">
                <label htmlFor="dateOfBirth">
                  Date of Birth
                </label>

                <DatePicker
                  name="dateOfBirth"
                  onChange={(date) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "dateOfBirth",
                      value: date,
                    });
                  }}
                  value={state.dateOfBirth}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="middleName"
                label="Middle Name"
                handleChange={handleInputChange}
                value={state.middleName}
                containerClasses="flex-half"
              />

              <TextInput
                name="weight"
                label="Weight (lb)"
                handleChange={handleInputChange}
                value={state.weight}
                type="number"
                step="0.1"
                min="2"
                max="660"
                className="mb-0 base-input"
                containerClasses="flex-half"
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                name="lastName"
                label="Last Name"
                required
                handleChange={handleInputChange}
                value={state.lastName}
                containerClasses="flex-half"
              />

              <div className="flex-half form-group">
                <label htmlFor="gender">
                  Gender
                </label>

                <Select
                  className="gender-select"
                  classNamePrefix="select"
                  options={genders}
                  placeholder="Gender"
                  onChange={handleGenderChange}
                  value={state.gender}
                  menuPosition="fixed"
                  isClearable
                />
              </div>
            </div>
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleUpdatePassenger}
                    disabled={state.saving}>
              Submit
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Passenger</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            Are you sure you want to remove this passenger from the prospect's profile?<br/>
            This action is permanent and cannot be undone.
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleDeletePassengerConfirm}
                    disabled={state.saving}>
              Delete Passenger
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default SingleProspectPassengersTable;
